import { useMemo } from 'react';

import { IContract, IProject } from 'store/projectContract.store';

import { useQueryGetProgramGeneralHierarchyList } from 'api/queries/getProgramGeneralHierarchyList.query';
import {
    PROGRESS_MEASUREMENT_METHODS_KEYS,
    type QueryResponse as ProgressMeasurementMethodOption,
} from 'api/queries/getProgressMeasurementMethods.query';

import { ProjectTreeNode } from 'components/common/TreeProgramFilter/ProjectTree/projectTree.utils';

import type { FormValues } from 'components/pages/ScheduleViewsPage/components/ViewConfiguration/components/ViewConfigurationDrawerForm/components/RowConfig/RowConfig';
import { RowConfigItem } from 'components/pages/ScheduleViewsPage/components/ViewConfiguration/components/ViewConfigurationDrawerForm/components/RowConfigView/RowConfigView';

function normalizeProgressFromApiRow(
    pm: { code?: string; methodName?: string; partial?: boolean } | undefined,
): { code: string; partial: boolean } | undefined {
    const code = pm?.code ?? pm?.methodName;
    if (code == null) return undefined;
    return { code, partial: pm?.partial ?? false };
}

export function buildProgressMeasurementFormValues(
    methods: ProgressMeasurementMethodOption[] | undefined,
    savedFromFirstRow?: { code: string; partial: boolean },
): { progressMeasurementCode: string; progressMeasurementPartial: boolean } {
    const code = savedFromFirstRow?.code ?? methods?.[0]?.key ?? PROGRESS_MEASUREMENT_METHODS_KEYS.CRITICALITY_WEIGHT;
    const partial =
        code === PROGRESS_MEASUREMENT_METHODS_KEYS.RESOURCE_UNITS ? false : (savedFromFirstRow?.partial ?? false);
    return { progressMeasurementCode: code, progressMeasurementPartial: partial };
}

function findProjectAndContract(
    hierarchyList:
        | {
              children?: Array<{
                  id: number;
                  name: string;
                  uuid: string;
                  children?: Array<{ id: number; name: string; uuid: string }>;
              }>;
          }
        | undefined,
    projectId: number,
    contractId: number,
): { project: IProject; contract: IContract } {
    const project = hierarchyList?.children?.find((p) => p.id === projectId);
    const contract = project?.children?.find((c) => c.id === contractId);
    return {
        project: project
            ? { id: project.id, title: project.name, uuid: project.uuid as ProjectTreeNode['uuid'] }
            : ({ id: projectId, title: '', uuid: '' } as IProject),
        contract: contract
            ? { id: contract.id, title: contract.name, uuid: contract.uuid as ProjectTreeNode['uuid'] }
            : ({ id: contractId, title: '', uuid: '' } as IContract),
    };
}

export const useGetRows = ({ api, viewId }) => {
    const { data: viewConfiguration } = api.getPoapViewById.instance({ viewId: Number(viewId) });
    const rowsConfiguration = viewConfiguration?.viewRows;

    const { data: projectHierarchyList } = useQueryGetProgramGeneralHierarchyList('ProgramLibraryUploadFiles');

    const { apiRows, savedProgressFromFirstRow } = useMemo(() => {
        if (!rowsConfiguration?.length) {
            return { apiRows: [] as RowConfigItem[], savedProgressFromFirstRow: undefined };
        }

        const sorted = [...rowsConfiguration].sort((a, b) => (a.rowOrder ?? 0) - (b.rowOrder ?? 0));
        const savedProgressFromFirstRow = normalizeProgressFromApiRow(sorted[0]?.progressMeasurement);

        const apiRows = sorted.map((row) => {
            const { project, contract } = findProjectAndContract(projectHierarchyList, row.projectId, row.contractId);
            return {
                data: {
                    project,
                    contract,
                    rowName: row.rowName,
                    rowType: row.rowType as FormValues['rowType'],
                    filterItem: row.filterItem as FormValues['filterItem'],
                    filterItemReferences: row.filterItemReferences,
                    latestVersionId: row.latestVersionId,
                    compareVersionId: row.compareVersionId,
                    rowId: row.rowId,
                },
            } as RowConfigItem;
        });

        return { apiRows, savedProgressFromFirstRow };
    }, [rowsConfiguration, projectHierarchyList]);

    return { rows: apiRows, savedProgressFromFirstRow };
};
